import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Textarea } from "@/components/ui/textarea";
import { ShoppingCart, Plus, Minus, Clock, AlertCircle, ImageIcon, CalendarDays, Ban } from "lucide-react";
import type { Product } from "@shared/schema";
import { format, addDays, startOfDay } from "date-fns";
import { sv } from "date-fns/locale";

type CartItem = {
  productId: string;
  product: Product;
  quantity: number;
};

export default function ClientOrder() {
  const { toast } = useToast();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [message, setMessage] = useState("");
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<string>("");

  const { data: products, isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ["/api/products"],
  });

  const { data: settings } = useQuery<{ orderWindowOpen: string; orderWindowClose: string; isOrderingOpen: boolean }>({
    queryKey: ["/api/settings/order-window"],
  });

  const { data: clientProfile } = useQuery<{ id: string; name: string; isSuspended: boolean } | null>({
    queryKey: ["/api/client-auth/me"],
  });

  const isSuspended = clientProfile?.isSuspended ?? false;
  const isOrderingOpen = settings?.isOrderingOpen ?? true;

  // Compute min/max for date picker
  const today = startOfDay(new Date());
  const minDaysOut = isOrderingOpen ? 1 : 2;
  const minDeliveryDate = addDays(today, minDaysOut);
  const minDeliveryDateStr = format(minDeliveryDate, "yyyy-MM-dd");

  const createOrderMutation = useMutation({
    mutationFn: async (data: { items: { productId: string; quantity: number }[]; notes?: string; deliveryDate?: string }) => {
      return apiRequest("POST", "/api/orders", data);
    },
    onSuccess: () => {
      toast({
        title: "Beställning skickad!",
        description: selectedDeliveryDate
          ? `Din order har registrerats för leverans ${format(new Date(selectedDeliveryDate), "d MMMM yyyy", { locale: sv })}.`
          : "Din order har registrerats.",
      });
      setCart([]);
      setMessage("");
      setSelectedDeliveryDate("");
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
    },
    onError: (error: unknown) => {
      let msg = "Kunde inte skicka beställningen. Försök igen.";
      if (error instanceof Error) {
        const match = error.message.match(/^\d+: (.+)$/);
        if (match) {
          try {
            const parsed = JSON.parse(match[1]) as { message?: string };
            msg = parsed.message || msg;
          } catch {
            msg = match[1] || msg;
          }
        }
      }
      toast({
        title: "Fel",
        description: msg,
        variant: "destructive",
      });
    },
  });

  const addToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.productId === product.id);
      if (existing) {
        return prev.map((item) =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { productId: product.id, product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.productId === productId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const getUnitLabel = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påse";
      case "box": return "låda";
      default: return unit;
    }
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      const price = parseFloat(item.product.unitPrice || "0");
      return total + price * item.quantity;
    }, 0);
  };

  const handleSubmitOrder = () => {
    if (cart.length === 0) return;
    if (!isOrderingOpen && !selectedDeliveryDate) {
      toast({
        title: "Välj leveransdatum",
        description: `Beställningsfönstret är stängt. Välj ett leveransdatum minst 2 dagar framåt för att lägga en framtida order.`,
        variant: "destructive",
      });
      return;
    }
    const items = cart.map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
    }));
    createOrderMutation.mutate({ 
      items, 
      notes: message.trim() || undefined,
      ...(selectedDeliveryDate ? { deliveryDate: selectedDeliveryDate } : {}),
    });
  };

  if (isSuspended) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Lägg beställning</h1>
          <p className="text-muted-foreground">Välj produkter och skicka din order</p>
        </div>
        <Card className="border-red-500/30 bg-red-500/5" data-testid="card-account-suspended">
          <CardContent className="pt-6 pb-6">
            <div className="flex items-start gap-4">
              <Ban className="h-6 w-6 text-red-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-red-700 dark:text-red-400">Ditt konto är spärrat</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Du kan inte lägga beställningar just nu. Kontakta vår kundtjänst för mer information.
                </p>
                <div className="mt-3 space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Telefon:</span>{" "}
                    <a href="tel:0739959992" className="text-primary hover:underline" data-testid="link-suspend-phone">073-995 99 92</a>
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">E-post:</span>{" "}
                    <a href="mailto:info@reefbageri.se" className="text-primary hover:underline" data-testid="link-suspend-email">info@reefbageri.se</a>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Lägg beställning</h1>
          <p className="text-muted-foreground">Välj produkter och skicka din order</p>
        </div>
        {settings && (
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Beställningar: {settings.orderWindowOpen} - {settings.orderWindowClose}
            </span>
            {isOrderingOpen ? (
              <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                Öppet
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                Stängt
              </Badge>
            )}
          </div>
        )}
      </div>

      {!isOrderingOpen && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Beställningsfönstret är stängt</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Du kan fortfarande lägga en framtida order. Välj ett leveransdatum minst 2 dagar framåt nedan — produktionen sker dagen innan vald leveransdag.
                </p>
                <p className="text-sm mt-2">
                  Vill du beställa till imorgon?{" "}
                  <a href="tel:0739959992" className="font-medium text-amber-600 hover:underline">
                    Ring oss på 073-995 99 92
                  </a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <h2 className="font-semibold">Produkter</h2>
          {productsLoading ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i}>
                  <CardContent className="pt-4">
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-4" />
                    <Skeleton className="h-9 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : products && products.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {products.filter(p => p.isActive).map((product) => {
                const cartItem = cart.find((item) => item.productId === product.id);
                return (
                  <Card key={product.id} className="hover-elevate overflow-hidden">
                    {product.imageUrl ? (
                      <div className="aspect-video w-full overflow-hidden bg-muted">
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-video w-full flex items-center justify-center bg-muted">
                        <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
                      </div>
                    )}
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start mb-2 gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium">{product.name}</h3>
                          {product.isNew && <Badge variant="default" data-testid={`badge-new-${product.id}`}>Nyhet</Badge>}
                        </div>
                        <Badge variant="secondary">
                          {product.unitPrice} kr (ink.)/{getUnitLabel(product.sellUnit)}
                        </Badge>
                      </div>
                      {product.description && (
                        <p className="text-sm text-muted-foreground mb-4">
                          {product.description}
                        </p>
                      )}
                      {cartItem ? (
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => updateQuantity(product.id, -1)}
                            data-testid={`button-decrease-${product.id}`}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <Input
                            type="number"
                            value={cartItem.quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              setCart((prev) =>
                                prev.map((item) =>
                                  item.productId === product.id
                                    ? { ...item, quantity: val }
                                    : item
                                ).filter((item) => item.quantity > 0)
                              );
                            }}
                            className="w-20 text-center"
                            min={0}
                            data-testid={`input-quantity-${product.id}`}
                          />
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => updateQuantity(product.id, 1)}
                            data-testid={`button-increase-${product.id}`}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          className="w-full"
                          variant="outline"
                          onClick={() => addToCart(product)}
                          data-testid={`button-add-${product.id}`}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Lägg till
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">Inga produkter tillgängliga just nu.</p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="lg:col-span-1">
          <Card className="sticky top-20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                Din varukorg
              </CardTitle>
              <CardDescription>
                {cart.length === 0
                  ? "Varukorgen är tom"
                  : `${cart.length} produkt${cart.length > 1 ? "er" : ""}`}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {cart.length > 0 ? (
                <>
                  <div className="space-y-3">
                    {cart.map((item) => (
                      <div
                        key={item.productId}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{item.product.name}</p>
                          <p className="text-muted-foreground">
                            {item.quantity} {getUnitLabel(item.product.sellUnit)} × {item.product.unitPrice} kr (ink.)
                          </p>
                        </div>
                        <p className="font-medium">
                          {(parseFloat(item.product.unitPrice || "0") * item.quantity).toFixed(2)} kr (ink.)
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between font-semibold">
                      <span>Totalt</span>
                      <span>{calculateTotal().toFixed(2)} kr</span>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5" />
                      Leveransdatum
                      {!isOrderingOpen && <span className="text-destructive">*</span>}
                    </label>
                    <Input
                      type="date"
                      value={selectedDeliveryDate}
                      min={minDeliveryDateStr}
                      onChange={(e) => setSelectedDeliveryDate(e.target.value)}
                      className="text-sm"
                      data-testid="input-delivery-date"
                    />
                    <p className="text-xs text-muted-foreground">
                      {isOrderingOpen
                        ? `Välj leveransdatum (tidigast ${format(minDeliveryDate, "d MMM", { locale: sv })}). Produktion sker dagen innan.`
                        : `Beställningsfönster stängt – välj datum minst 2 dagar framåt (tidigast ${format(minDeliveryDate, "d MMM", { locale: sv })}).`}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Meddelande (valfritt)</label>
                    <Textarea
                      placeholder="Skriv ett meddelande till bageriet..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={2}
                      className="resize-none text-sm"
                      data-testid="input-order-message"
                    />
                  </div>
                  <Button
                    className="w-full"
                    onClick={handleSubmitOrder}
                    disabled={createOrderMutation.isPending || (!isOrderingOpen && !selectedDeliveryDate)}
                    data-testid="button-submit-order"
                  >
                    {createOrderMutation.isPending ? "Skickar..." : 
                      selectedDeliveryDate ? "Schemalägg beställning" : "Skicka beställning"}
                  </Button>
                </>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Lägg till produkter från listan
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
